import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import {
  getUploadUrl,
  uploadFileToS3,
  createUploadedReceipt,
} from '../../../apis/fileApi';
import { GetUploadUrlRequest, CreateReceiptRequest } from '../../../apis/types';

export interface UploadResult {
  storageKey: string;
  receiptId: string;
}

export const useUploadFile = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [progress, setProgress] = useState<number>(0);

  const {
    mutateAsync: uploadFileMutation,
    isPending: uploading,
    error,
  } = useMutation({
    mutationFn: async ({
      file,
      folder = 'receipts',
    }: {
      file: File;
      folder?: string;
    }): Promise<UploadResult> => {
      const token = await getAccessTokenSilently();

      // Reset progress
      setProgress(0);

      // Step 1: Get presigned URL
      const uploadUrlRequest: GetUploadUrlRequest = {
        fileName: file.name,
        contentType: file.type,
        folder,
      };

      const presignedResponse = await getUploadUrl(uploadUrlRequest, token);

      // Step 2: Upload file to S3
      await uploadFileToS3(presignedResponse.url, file, uploadProgress => {
        setProgress(uploadProgress);
      });

      // Step 3: Create receipt record
      const receiptRequest: CreateReceiptRequest = {
        storageKey: presignedResponse.key,
        contentType: file.type,
        originalFileName: file.name,
        fileSizeInBytes: file.size,
      };

      const receipt = await createUploadedReceipt(receiptRequest, token);

      setProgress(100);
      return { storageKey: presignedResponse.key, receiptId: receipt.id };
    },
    onError: error => {
      console.error('Error uploading file:', error);
      setProgress(0);
    },
    onSuccess: () => {
      // Reset progress after a delay
      setTimeout(() => setProgress(0), 1000);
    },
  });

  const uploadFile = useCallback(async (
    file: File,
    folder?: string
  ): Promise<UploadResult | null> => {
    try {
      return await uploadFileMutation({ file, folder });
    } catch (error) {
      return null;
    }
  }, [uploadFileMutation]);

  return {
    uploadFile,
    uploading,
    progress,
    error: error?.message || null,
  };
};
